/**
 * PianoKeyboard - Clavier piano visuel pour sélection de notes
 *
 * Usage:
 *   const piano = new PianoKeyboard({
 *     startOctave: 1,
 *     endOctave: 3,
 *     onNoteSelect: (note) => console.log('Selected:', note)
 *   });
 *   piano.render(container);
 *   piano.setSelectedNote('C2');
 */

export class PianoKeyboard {
    constructor(options = {}) {
        this.startOctave = options.startOctave || 1;
        this.endOctave = options.endOctave || 3;
        this.onNoteSelect = options.onNoteSelect || (() => {});
        this.selectedNote = options.selectedNote || null;
        this.container = null;
        this.element = null;

        // Notes dans une octave
        this.noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        this.blackKeys = ['C#', 'D#', 'F#', 'G#', 'A#'];
    }

    /**
     * Génère toutes les notes du clavier
     */
    _generateNotes() {
        const notes = [];
        for (let octave = this.startOctave; octave <= this.endOctave; octave++) {
            for (const name of this.noteNames) {
                notes.push(`${name}${octave}`);
            }
        }
        // Ajouter C de l'octave suivante pour compléter
        notes.push(`C${this.endOctave + 1}`);
        return notes;
    }

    /**
     * Vérifie si une note est une touche noire
     */
    _isBlackKey(note) {
        const noteName = note.replace(/\d+$/, '');
        return this.blackKeys.includes(noteName);
    }

    /**
     * Calcule la position d'une touche noire
     */
    _getBlackKeyOffset(note) {
        const noteName = note.replace(/\d+$/, '');
        const offsets = {
            'C#': 0.6,
            'D#': 1.8,
            'F#': 3.6,
            'G#': 4.7,
            'A#': 5.8
        };
        return offsets[noteName] || 0;
    }

    render(container) {
        this.container = container;

        const notes = this._generateNotes();
        const whiteKeys = notes.filter(n => !this._isBlackKey(n));
        const blackKeys = notes.filter(n => this._isBlackKey(n));

        // Calculer la largeur d'une touche blanche
        const whiteKeyWidth = 100 / whiteKeys.length;

        this.element = document.createElement('div');
        this.element.className = 'piano-keyboard';

        // Créer les touches blanches
        let whiteIndex = 0;
        for (const note of whiteKeys) {
            const key = document.createElement('div');
            key.className = 'piano-key white';
            key.dataset.note = note;
            key.style.width = `${whiteKeyWidth}%`;
            key.style.left = `${whiteIndex * whiteKeyWidth}%`;

            // Label pour les C
            if (note.startsWith('C')) {
                const label = document.createElement('span');
                label.className = 'key-label';
                label.textContent = note;
                key.appendChild(label);
            }

            key.addEventListener('click', () => this._onKeyClick(note));
            key.addEventListener('mouseenter', () => this._onKeyHover(note, true));
            key.addEventListener('mouseleave', () => this._onKeyHover(note, false));

            if (note === this.selectedNote) {
                key.classList.add('selected');
            }

            this.element.appendChild(key);
            whiteIndex++;
        }

        // Créer les touches noires
        for (const note of blackKeys) {
            const octave = parseInt(note.match(/\d+$/)[0]);
            const noteName = note.replace(/\d+$/, '');

            // Calculer la position basée sur l'octave et le nom de la note
            const octaveOffset = (octave - this.startOctave) * 7; // 7 touches blanches par octave
            const noteOffset = this._getBlackKeyOffset(note);
            const position = (octaveOffset + noteOffset) * whiteKeyWidth;

            const key = document.createElement('div');
            key.className = 'piano-key black';
            key.dataset.note = note;
            key.style.left = `${position}%`;
            key.style.width = `${whiteKeyWidth * 0.6}%`;

            key.addEventListener('click', (e) => {
                e.stopPropagation();
                this._onKeyClick(note);
            });
            key.addEventListener('mouseenter', () => this._onKeyHover(note, true));
            key.addEventListener('mouseleave', () => this._onKeyHover(note, false));

            if (note === this.selectedNote) {
                key.classList.add('selected');
            }

            this.element.appendChild(key);
        }

        container.appendChild(this.element);
    }

    _onKeyClick(note) {
        this.setSelectedNote(note);
        this.onNoteSelect(note);
    }

    _onKeyHover(note, isHovering) {
        // Optionnel: preview sonore ou highlight visuel
    }

    /**
     * Met à jour la note sélectionnée visuellement
     */
    setSelectedNote(note) {
        this.selectedNote = note;

        if (!this.element) return;

        // Retirer la sélection précédente
        this.element.querySelectorAll('.piano-key.selected').forEach(key => {
            key.classList.remove('selected');
        });

        // Ajouter la nouvelle sélection
        const selectedKey = this.element.querySelector(`[data-note="${note}"]`);
        if (selectedKey) {
            selectedKey.classList.add('selected');
        }
    }

    /**
     * Retourne la note sélectionnée
     */
    getSelectedNote() {
        return this.selectedNote;
    }

    /**
     * Détruit le composant
     */
    destroy() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        this.element = null;
    }
}
